import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import NewExpense from "../Expenses/NewExpense.js";
import NewOrder from "../Orders/NewOrder.js";
import NewProduct from "../Products/NewProduct.js";
import GrossSalesByDate from "./reports/GrossSalesByDate.js";
import ExpensesByDate from "./reports/ExpensesByDate.js";
import NetSalesByDate from "./reports/NetSalesByDate.js";
import GrossSalesBySalesperson from "./reports/GrossSalesBySalesperson.js";
import TopTenByDate from "./reports/TopTenByDate.js";
import OnboardingOverlay from './components/OnboardingOverlay';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  Pane,
  Card,
  Heading,
  Button,
  SideSheet,
  TextInput,
  AddIcon,
  Paragraph,
} from 'evergreen-ui';

const GET_PUBLISHER_ONBOARDING_STATUS = gql`
  query GetPublisherOnboardingStatus($publisherId: ID!) {
    publisherOnboardingStatus(publisherId: $publisherId)
  }
`;

function Dash() {
  const navigate = useNavigate();

  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const [newOrderMode, setNewOrderMode] = useState(false);
  const [newExpenseMode, setNewExpenseMode] = useState(false);
  const [newProductMode, setNewProductMode] = useState(false);
  const [orderCustomer, setOrderCustomer] = useState("");
  const [orderId, setOrderId] = useState("");
  const [productTitle, setProductTitle] = useState("");

  // Initialize showOnboarding to false to prevent flash
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Add a state to track if we've checked the onboarding status
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Add a state to trigger dashboard data refresh
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { data: onboardingData, refetch: refetchOnboardingStatus } = useQuery(GET_PUBLISHER_ONBOARDING_STATUS, {
    variables: { publisherId: currentpublisher.id },
    onCompleted: (data) => {
      // Mark that we've checked the onboarding status
      setOnboardingChecked(true);

      // Check localStorage first to avoid showing onboarding if user has completed it
      const savedStatus = localStorage.getItem(`onboarding_${currentpublisher.id}`);
      if (savedStatus === 'completed') {
        setShowOnboarding(false);
        return;
      }

      // Otherwise, use the database value
      const onboardingCompleted = data.publisherOnboardingStatus;
      setShowOnboarding(!onboardingCompleted);

      // Save to localStorage to prevent showing on refresh if completed
      if (onboardingCompleted) {
        localStorage.setItem(`onboarding_${currentpublisher.id}`, 'completed');
      }
    }
  });

  // Set up event listener for dashboard data refresh
  useEffect(() => {
    const handleRefreshDashboardData = () => {
      // Increment refresh trigger to cause dashboard components to re-render
      setRefreshTrigger(prev => prev + 1);

      // Refetch any queries that need to be updated
      refetchOnboardingStatus();
    };

    // Add event listener
    window.addEventListener('refreshDashboardData', handleRefreshDashboardData);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('refreshDashboardData', handleRefreshDashboardData);
    };
  }, [refetchOnboardingStatus]);

  const handleToggleNewOrderMode = () => {
    setNewOrderMode(!newOrderMode);
  }

  const handleToggleNewExpenseMode = () => {
    setNewExpenseMode(!newExpenseMode);
  }

  const handleToggleNewProductMode = () => {
    setNewProductMode(!newProductMode);
  }

  // QUICK START - Search
  const handleSearchProductTitle = () => {
    navigate('/products', {state: { query: {title: productTitle}, queryTitle: `Titles containing: ${productTitle}` }});
  }
  const handleSearchOrderCompany = () => {
    navigate('/orders', {state: { query: {customer__company: orderCustomer}, queryTitle: `Company name containing: ${orderCustomer}` }});
  }
  const handleSearchOrderId = () => {
    navigate(`/orders/${orderId}`);
  }

  // TODO: Get rid of this
  const fakeHandler = () => {
    console.log("fake handled");
  }

  const getDateBounds = () => {
    const today = new Date();

    // Start of today (midnight)
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);

    // End of today (23:59:59.999)
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);

    // Yesterday start
    const startOfYesterday = new Date(startOfDay);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    return {
      today: today.toISOString().split('T')[0],
      startOfToday: startOfDay.toISOString(),
      endOfToday: endOfDay.toISOString(),
      startOfYesterday: startOfYesterday.toISOString()
    };
  };

  const dates = getDateBounds();

  return (
    <Pane padding={16}>
      {onboardingChecked && showOnboarding && (
        <OnboardingOverlay
          onDismiss={() => setShowOnboarding(false)}
          publisherId={currentpublisher.id}
        />
      )}
      {newOrderMode && (
        <NewOrder
          handleToggleNewMode={handleToggleNewOrderMode}
          handleRefetchOrders={fakeHandler}
          publisher={currentpublisher.id}
        />
      )}
      {/* {newExpenseMode && (
        <NewExpense
          handleToggleNewMode={handleToggleNewExpenseMode}
          handleRefetchExpenses={fakeHandler}
          publisher={currentpublisher.id}
        />
      )} */}
      <SideSheet isShown={newExpenseMode} onCloseComplete={() => setNewExpenseMode(false)}>
        <NewExpense
          handleToggleNewMode={handleToggleNewExpenseMode}
          handleRefetchExpenses={fakeHandler}
          publisher={currentpublisher.id}
        />
      </SideSheet>


      {newProductMode && (
        <NewProduct
          handleToggleNewMode={handleToggleNewProductMode}
          handleRefetchProducts={fakeHandler}
          publisher={currentpublisher.id}
        />
      )}
      {!newProductMode && !newOrderMode && (
        <Pane>
          <Pane display="flex" flexDirection="row">
            <Card background="white" elevation={3} padding={16} marginRight={16}>
              <Pane display="flex" alignItems="center" justifyContent="space-between">
                <Paragraph size={500} fontWeight="bolder" marginRight={8} color="var(--darkgreen)">QUICK START</Paragraph>
              </Pane>
              <Pane display="flex" marginTop={8} alignItems="center">
                {/* <Tooltip content={"Temporarily disabled for site update."} ><HelpIcon color="muted" marginRight={16} /></Tooltip> */}

                <Pane marginRight={8}>
                  <Button iconBefore={AddIcon} className="new-button" onClick={e => setNewOrderMode(true)}>NEW ORDER</Button>
                </Pane>
                <Pane marginX={8}>
                  <Button iconBefore={AddIcon} className="new-button" onClick={e => navigate('/products', {state: {openNewProductForm: true}})}>NEW PRODUCT</Button>
                </Pane>
                <Pane marginLeft={8}>
                  <Button iconBefore={AddIcon} className="new-button" onClick={e => setNewExpenseMode(true)}>NEW EXPENSE</Button>
                </Pane>
              </Pane>
              <Pane display="flex" marginTop={16}>

                <Pane id="quick-links" marginRight={32}>
                  <Paragraph size={400} fontWeight="bold" marginBottom={8}>View</Paragraph>

                  <Heading size={300} marginY={8}>
                    <Link to={{
                      pathname: '/expenses',
                      state: {
                        query: {
                          created__gte: dates.startOfToday,
                          created__lte: dates.endOfToday
                        },
                        queryTitle: `Expenses Created Today (${dates.today})`
                      }
                    }}>
                      &gt; Expenses Created Today
                    </Link>
                  </Heading>
                  <Pane display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    <Heading size={300} marginY={8}>
                      <Link to={{
                        pathname: '/orders',
                        state: {
                          query: {
                            created__gte: dates.startOfToday,
                            created__lte: dates.endOfToday
                          },
                          queryTitle: `Orders Created Today (${dates.today})`
                        }
                      }}>
                        &gt; Orders Created Today
                      </Link>
                    </Heading>
                  </Pane>
                  <Pane display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    {/* <Heading size={400} marginY={8}>
                <Link to='/orders?shipped=false&paid=true'>View Orders Awaiting Shipment &gt;</Link>
              </Heading> */}
                    <Heading size={300} marginY={8}>
                      <Link to={{
                        pathname: '/orders',
                        state: { query: {shipped: false, paid: true}, queryTitle: "Orders Awaiting Shipment" }
                      }}>
                        &gt; Orders Awaiting Shipment
                      </Link>
                    </Heading>
                  </Pane>
                  <Pane display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    <Heading size={300} marginY={8}>
                      <Link to={{
                        pathname: '/orders',
                        state: { query: {backordered: true}, queryTitle: "Backorders" }
                      }}>
                        &gt; Backorders
                      </Link>
                    </Heading>
                  </Pane>

                </Pane>

                <Pane id="quick-searches">
                  <Paragraph size={400} fontWeight="bold" marginBottom={4} >Search</Paragraph>
                  <Paragraph size={300} >Find Products by Title</Paragraph>
                  <Pane display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                    <TextInput
                      onChange={e => setProductTitle(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") handleSearchProductTitle();
                      }}
                      width={200}
                      value={productTitle}
                      marginY={4}
                    />
                    <Button onClick={handleSearchProductTitle}>Go</Button>
                  </Pane>
                  <Paragraph size={300} >Find Orders by Company</Paragraph>
                  <Pane display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                    <TextInput
                      onChange={e => setOrderCustomer(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") handleSearchOrderCompany();
                      }}
                      width={200}
                      value={orderCustomer}
                      marginY={4}
                    />
                    <Button onClick={handleSearchOrderCompany}>Go</Button>
                  </Pane>

                  <Paragraph size={300} >Find Order by ID</Paragraph>
                  <Pane display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                    <TextInput
                      onChange={e => setOrderId(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") handleSearchOrderId();
                      }}
                      width={200}
                      value={orderId}
                      marginY={4}
                    />
                    <Button onClick={handleSearchOrderId}>Go</Button>
                  </Pane>
                </Pane>
              </Pane>
            </Card>
            <GrossSalesBySalesperson />
            <TopTenByDate />

          </Pane>
          <Pane display="flex" flexDirection="row" marginTop={16}>
            <GrossSalesByDate />
            <NetSalesByDate />
            <ExpensesByDate />
          </Pane>
        </Pane>
      )}

    </Pane>
  );
}

export default Dash;
